import { ChevronDown } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { AppShell } from '@/components/layout/AppShell';
import { cn } from '@/lib/utils';
import { useUiStore } from '@/stores/uiStore';

import { useSession } from '../api/useSession';
import { SessionMobilePanel } from '../components/SessionMobilePanel';
import { SessionStepSidebar } from '../components/SessionStepSidebar';
import { displayApplicantName } from '../sessionReference';

// Concrete default so the `?? PENDING_PILL` fallback is never undefined
// (under noUncheckedIndexedAccess, indexed/record access is `T | undefined`).
const PENDING_PILL = {
  cls: 'bg-[#DA74001A] text-[#DA7400] ring-[#DA7400]',
  dot: 'bg-[#D97706]',
  label: 'Pending Review',
};

const STATUS_PILL: Record<string, { cls: string; dot: string; label: string }> = {
  InProgress: {
    cls: 'bg-[#0048FF1A] text-[#0048FF] ring-[#0048FF]',
    dot: 'bg-[#0048FF]',
    label: 'In Progress',
  },
  PendingApproval: PENDING_PILL,
  Completed: {
    cls: 'bg-[#00C2321A] text-[#00C232] ring-[#00C232]',
    dot: 'bg-[#00C232]',
    label: 'Completed',
  },
  Rejected: {
    cls: 'bg-[#BF36361A] text-[#BF3636] ring-[#BF3636]',
    dot: 'bg-[#BF3636]',
    label: 'Rejected',
  },
};

/**
 * Review page — reviewer takes action on a session.
 * Approve / Reject / Hold buttons will be added to the action bar below the sub-header.
 * Layout identical to View but kept separate so reviewer actions can be added here
 * without touching the read-only View page.
 */
export function SessionReviewScreen() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const setSidebarCollapsed = useUiStore((s) => s.setSidebarCollapsed);
  const { data: session, isLoading } = useSession(sessionId);

  useEffect(() => {
    const prev = useUiStore.getState().sidebarCollapsed;
    setSidebarCollapsed(true);
    return () => setSidebarCollapsed(prev);
  }, [setSidebarCollapsed]);

  const [stepIdx, setStepIdx] = useState(0);

  // The verified name, matching the list and the detail view. While loading, show a
  // placeholder rather than "NA" — nothing is known yet, which is not the same as
  // "no name was verified".
  const applicantName = isLoading ? '…' : displayApplicantName(session?.applicantFullName);
  const pill = STATUS_PILL[session?.status ?? ''] ?? PENDING_PILL;
  const sidebarSteps = session?.steps
    .slice()
    .sort((a, b) => a.position - b.position)
    .map((s) => ({ id: s.id, displayName: s.displayName, status: s.status }));

  return (
    <AppShell title="Sessions" backTo="/sessions" noPadding>
      <div className="flex h-full w-full flex-col gap-[clamp(0.625rem,0.9vw,1rem)] p-[clamp(0.625rem,0.9vw,1rem)]">
        {/* ── Sub-header ── */}
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-[clamp(12px,1vw,16px)]">
          {/* No back arrow here — the Topbar owns it now (AppShell `backTo`). Keeping this
              one as well put two identical chevrons on the same screen, a few pixels apart. */}
          <div className="flex items-center gap-[clamp(12px,1vw,16px)]">
            <div className="flex items-center gap-2.5">
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#E0E7FF] text-xs font-semibold text-[#4F46E5]">
                {applicantName.charAt(0).toUpperCase()}
              </div>
              <span className="text-[length:var(--text-18)] font-semibold text-[#041A3A]">
                {applicantName}
              </span>
            </div>
          </div>

          {/* Right side: status pill + reviewer action buttons (add here) */}
          <div className="flex items-center gap-2">
            {/* ── Reviewer actions go here ──────────────────────────────────
                Example future buttons:
                <button className="...approve styles...">Approve</button>
                <button className="...reject styles...">Reject</button>
                <button className="...hold styles...">Hold</button>
            ─────────────────────────────────────────────────────────────── */}
            <button
              type="button"
              className={cn(
                'flex cursor-pointer items-center gap-2 rounded-lg px-[clamp(10px,0.8vw,12px)] py-[clamp(6px,0.6vh,8px)] text-[length:var(--text-16)] font-semibold ring-1',
                pill.cls,
              )}
            >
              <span className={cn('h-2 w-2 rounded-full', pill.dot)} />
              {pill.label}
              <ChevronDown size={13} className="ml-0.5" />
            </button>
          </div>
        </div>

        {/* ── Two-column body ── */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-[clamp(12px,1vw,16px)] overflow-hidden lg:flex-row">
          {/* Sidebar — fixed width, full height, scrolls its own list */}
          <div className="hidden w-[clamp(280px,22vw,328px)] shrink-0 lg:flex lg:h-full lg:flex-col">
            <SessionStepSidebar
              steps={sidebarSteps}
              activeIndex={stepIdx}
              onStepClick={setStepIdx}
            />
          </div>

          {/* Right panel — h-full so overflow-y-auto clips here, not the page */}
          <div className="min-w-0 flex-1 h-full">
            {session ? (
              <SessionMobilePanel
                session={session}
                stepIndex={stepIdx}
                onStepChange={setStepIdx}
                mode="review"
              />
            ) : (
              <div className="flex h-full items-center justify-center rounded-xl border border-[#E4E4E4] bg-white p-6 text-sm text-[#666666]">
                {isLoading ? 'Loading session…' : 'Session not found.'}
              </div>
            )}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
